import React from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Grid, Loader } from 'semantic-ui-react';
import LineChart from 'components/common/charts/LineChart/Container';
import {
  blueColor,
  greenColor,
  orangeColor,
} from 'utils/chartColors';
import currencyFormat from 'utils/currencyFormat';
import todaysDate from 'utils/todaysDate';
import toMonthStat from 'helpers/toMonthStat';
import walletGrowthLabel, {
  walletChartData,
} from 'helpers/walletGrowthLabel';

import './index.scss';

const SingleGroupWalletChart = ({
  monthlyBalances,
  group,
  filterYear,
  loading,
}) => {
  const { push } = useHistory();

  const {
    id: groupId,
    organization_id: organizationId,
    wallet_balance: walletBalance = 0,
    savings_balance: savingsBalance = 0,
    social_funds_balance: socialFundsBalance = 0,
  } = group || {};

  const balances = monthlyBalances || [];

  const currentSavings = walletChartData(balances, 'current_savings');
  const currentSocialFunds = walletChartData(
    balances,
    'current_social_funds',
  );
  const currentWallet = walletChartData(
    balances,
    'current_wallet_balance',
  );

  const growthLabel = key => walletGrowthLabel(balances, key);

  const toActivities = purpose =>
    push(
      `/organizations/${organizationId}/groups/${groupId}/activities`,
      { purpose },
    );

  if (loading) {
    return (
      <div
        className="flex align-center justify-center"
        style={{ height: 300 }}
      >
        <Loader active inline="centered" />
      </div>
    );
  }

  return (
    <div className="group-wallet-charts">
      <Grid columns={3}>
        <Grid.Row stretched>
          <Grid.Column>
            <LineChart
              title="Total Wallet Balance"
              date={todaysDate(filterYear)}
              subtitle={growthLabel('current_wallet_balance')}
              amount={currencyFormat(walletBalance)}
              data={toMonthStat(currentWallet)}
              background={blueColor}
              dataKey="total"
              color="#fff"
              viewMoreRedirect
              view="general"
              viewKey1="activities"
              onViewMoreClicked={() => toActivities('saving')}
            />
          </Grid.Column>
          <Grid.Column>
            <LineChart
              title="Saving Balance"
              date={todaysDate(filterYear)}
              subtitle={growthLabel('current_savings')}
              amount={currencyFormat(savingsBalance)}
              data={toMonthStat(currentSavings)}
              background={greenColor}
              dataKey="total"
              color="#fff"
              viewMoreRedirect
              view="general"
              viewKey1="activities"
              onViewMoreClicked={() => toActivities('saving')}
            />
          </Grid.Column>
          <Grid.Column>
            <LineChart
              title="Social Fund Balance"
              date={todaysDate(filterYear)}
              subtitle={growthLabel('current_social_funds')}
              amount={currencyFormat(socialFundsBalance)}
              data={toMonthStat(currentSocialFunds)}
              background={orangeColor}
              dataKey="total"
              color="#fff"
              viewMoreRedirect
              view="general"
              viewKey1="activities"
              onViewMoreClicked={() => toActivities('social_fund')}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    </div>
  );
};

SingleGroupWalletChart.defaultProps = {
  monthlyBalances: [],
  group: null,
  filterYear: null,
  loading: false,
};

SingleGroupWalletChart.propTypes = {
  monthlyBalances: PropTypes.arrayOf(
    PropTypes.shape({
      month: PropTypes.string,
      current_savings: PropTypes.number,
      current_social_funds: PropTypes.number,
      current_wallet_balance: PropTypes.number,
    }),
  ),
  filterYear: PropTypes.number,
  loading: PropTypes.bool,
  group: PropTypes.shape({
    id: PropTypes.number,
    organization_id: PropTypes.number,
    wallet_balance: PropTypes.number,
    savings_balance: PropTypes.number,
    social_funds_balance: PropTypes.number,
  }),
};

export default SingleGroupWalletChart;
